import classNames from "classnames";
import type { AppDescription} from "common/components/SerializedApp";
import { AppKind, defaultUserFeatures, getAppKind } from "common/components/SerializedApp";
import type { DataStore } from "common/components/types";
import type { BuilderActionsForApp } from "common/Database/builder-actions";
import { eminenceFlags } from "common/Database/eminence";
import { makeSimpleAppDescriptionContext } from "common/generator/components";
import type { TypeSchema } from "common/generator/description";
import type { WireNavigationModel } from "common/wire";
import copyToClipboard from "copy-to-clipboard";
import { useChangeObservable } from "hooks/use-change-observable";
import React from "react";
import toast from "react-hot-toast";
import tw from "twin.macro";
import { WireBackendImpl } from "webapp/hydrated-ui/backend";
import { getAppFacilities } from "webapp/lib/app-renderer";
import AudioPlayer from "webapp/lib/audio";
import { localStorageGetItem, localStorageSetItem } from "webapp/lib/local-storage";
import { css } from "webapp/styled-components";
import { useCleanupMemo } from "./use-cleanup-memo";

interface WireBackendResult {
    readonly backend?: WireBackendImpl;
    readonly urlStack: string[];
    readonly onBack: () => void;
}

export function useWireBackend(
    appID: string,
    appDescription: AppDescription | undefined,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema | undefined,
    dataStore: DataStore | undefined,
    isBuilder: boolean,
    startPath?: string
): WireBackendResult {
    const urlStack = React.useRef<string[]>(startPath === undefined ? [] : [startPath]);

    const backendRef = React.useRef<WireBackendImpl>();
    const onBack = React.useCallback(() => {
        urlStack.current.pop();
        backendRef.current?.urlPathChanged(urlStack.current.slice(-1)[0] ?? "");
    }, []);

    const backend = useCleanupMemo(() => {
        if (
            appDescription === undefined ||
            getAppKind(appDescription) !== AppKind.Page ||
            schema === undefined ||
            dataStore === undefined
        ) {
            return [undefined, () => undefined];
        }
        const adc = makeSimpleAppDescriptionContext(
            appID,
            AppKind.Page,
            appDescription,
            builderActions,
            schema,
            defaultUserFeatures,
            eminenceFlags.pro
        );
        const result = new WireBackendImpl(
            appID,
            getAppFacilities(),
            dataStore,
            adc,
            {
                openLink: url => window.open(url),
                showToast: (success: boolean, message: string) => {
                    toast.custom(t => (
                        <div
                            className={classNames(t.visible ? "enter" : "leave", success && "success")}
                            css={css`
                                &.success {
                                    ${tw`bg-g400`}
                                }

                                &.enter {
                                    ${tw`animate-enter`}
                                }

                                &.leave {
                                    ${tw`animate-leave`}
                                }
                            `}
                            tw="bg-r400 px-3 py-2 text-white font-medium text-sm rounded-md shadow-md-dark">
                            {message}
                        </div>
                    ));

                    // toast(content, {});
                },
                copyToClipboard: data => copyToClipboard(data),
                playSound: sound => {
                    const player = new AudioPlayer(sound);
                    player.play();
                },
                loadStateValues: key => {
                    try {
                        const s = localStorageGetItem(`state-${key}`);
                        if (s === undefined) return {};
                        return JSON.parse(s);
                    } catch {
                        return {};
                    }
                },
                saveStateValues: (key, values) => localStorageSetItem(`state-${key}`, JSON.stringify(values)),
            },
            isBuilder,
            backendRef.current ?? urlStack.current.slice(-1)[0]
        );
        return [result, () => result.retire()];
    }, [appDescription, appID, builderActions, dataStore, isBuilder, schema]);

    backendRef.current = backend;

    return React.useMemo(() => ({ backend, urlStack: urlStack.current, onBack }), [backend, urlStack, onBack]);
}

export const WireBackendContext = React.createContext<WireBackendResult>({ urlStack: [], onBack: () => undefined });

export function useWireBackendContext(): WireBackendResult {
    return React.useContext(WireBackendContext);
}

export function useWireNavigationModel(
    backend: WireBackendImpl | undefined,
    urlStack: string[]
): WireNavigationModel | undefined {
    const navModel = useChangeObservable(backend?.wireNavigationModelObservable);

    const urlPath = navModel?.urlPath ?? "";
    React.useEffect(() => {
        if (urlPath !== urlStack.slice(-1)[0]) {
            urlStack.push(urlPath);
        }
    }, [urlPath, urlStack]);

    return navModel;
}

export const WireNavigationModelContext = React.createContext<WireNavigationModel | undefined>(undefined);

export function useWireNavigationModelContext(): WireNavigationModel | undefined {
    return React.useContext(WireNavigationModelContext);
}
